import {
  Box,
  Grid,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FlexBetween from "../../../components/FlexBetween";
import useFetchFileProcessing from "../../../hooks/useFetchFileProcessing";
import useFetchRuleById from "../../../hooks/useFetchRuleById";
import {
  backgroundExecuteRuleReRun,
  executeRuleReRun,
} from "../../../services/rulesService";
import ResultStorageParameters from "../../../components/Rules/ResultStorageParameters";
import RunParameters from "../../../components/Rules/RunParameters";
import ScoringConfigOverrideAccordion from "../../../components/Scoring/ScoringConfigOverrideAccordion";
import { getComparisonScore } from "../../../services/scoringService";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  IconBtnEditBase,
  IconPencilGray,
  IconPlusGray,
  IconViewSvgWhite,
} from "../../../common/utils/icons";
import { apiType, sqlDatabaseType } from "../../../services/constants";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RuleInlineVariables from "../../../components/Rules/RuleInlineVariables";
import { useToast } from "../../../services/utils";
import ViewPayloadDialog from "../../../components/Dialogs/ViewPayloadDialog";
import { validateEngineRequest } from "../../../services/resourcesService";

import Loader from "../../../components/Molecules/Loader/Loader";
import useFetchAllConnectionKey from "../../../hooks/useFetchAllConnectionKey";
import CustomAccordian from "../../../components/Molecules/Accordian/CustomAccordion";
import FilterRules from "../../../components/Resource/FilterRules";
import AddMultipleGridList from "../../../components/AddMultipleGridList";
import ReadMoreLess from "../../../components/ReadMoreLess";
import { useBreadCrumbContext } from "../../../contexts/BreadCrumbContext";
import { useRuleResourceContext } from "../../../contexts/RuleResourceContext";
import { useSystemContext } from "../../../contexts/TagAndSystemProvider";
import useFetchAllSystem from "../../../hooks/useFetchAllSystem";
import CustomMultipleResourceRuleFilters from "../../../components/Rules/CustomMultipleResourceRuleFilters";
import SecondaryMergeResourceDialog from "../../../components/Dialogs/SecondaryMergeResourceDialog";
import useFetchResources from "../../../hooks/useFetchResources";
import useFetchAllResourcesWithoutDomain from "../../../hooks/useFetchAllResourcesWithoutDomain";
import useFetchLinkedServices from "../../../hooks/useFetchLinkedServices";
import useFetchResourceColumnsByMultipleIds from "../../../hooks/useFetchResourceColumnsByMultipleIds";
import useFetchComparisonRerunResultByRuleId from "../../../hooks/useFetchComparisionRerunResultByRuleId";
import { defaultResultStorageParameters } from "../../../services/models/execution.model";
import {
  getDefaultRunParameters,
  getQueryParams,
} from "../../../config/runParametersConfig";
import { getSecondaryMergeResourcePayloadFields } from "../../../services/utils/processRuleExecutionResourcesData";
import { IResultStorageParameters } from "../../../types/Execution";
import ReRunRuleExecutionTab from "../../../components/Rules/ReRun/ReRunRuleExecutionTab";
import { executionNameSchema } from "../../../schemas";
import ExecutionName from "../../../components/Molecules/Rule/ExecutionName";
import { useAuth } from "../../../contexts/AuthContext";
import RuleResearchQuery from "../RuleResearchQuery";
import useFetchAllConnectionKeyWithoutId from "../../../hooks/useFetchAllConnectionKeyWithoutId";
interface FilterFormData {
  filter_rules: any[];
}

const ReRunRuleExecution = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const { ruleId: currentRuleId } = useParams();
  const { isAdmin } = useAuth();
  const { showToast } = useToast();
  const {
    isResourceEdit,
    viewInlineVariables,
    setViewInlineVariables,
    setAllResourcesData,
    setLinkedServicesData,
    fileProcessingData,
    setFileProcessingData,
    setFetchedAllConnectionKeys,
    setResourceColumnsData,
  } = useRuleResourceContext();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isBackdropLoading, setIsBackdropLoading] = useState<boolean>(false);
  const [resourcesData, setResourcesData] = useState<any[]>([]);
  const [isEditResource, setIsEditResource] = useState(false);
  const [resultStorageParameters, setResultStorageParameters] =
    useState<IResultStorageParameters>(defaultResultStorageParameters);
  const [ruleExecutionPayload, setRuleExecutionPayload] = useState<any>({});
  const [runParameters, setRunParameters] = useState<any>(
    getDefaultRunParameters("ruleExecution"),
  );
  const [severityColumnNames, setSeverityColumnNames] = useState<any>({
    low: null,
    medium: null,
    high: null,
  });

  const [secondaryMergeResourceDialog, setSecondaryMergeResourceDialog] =
    useState(false);
  const [resourceDataWithSecMerge, setResourceDataWithSecMerge] = useState<any>(
    {},
  );
  const [currentDomainId, setCurrentDomainId] = useState(null);
  const [executionName, setExecutionName] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: any }>({
    execution_name: "",
  });
  const [ruleData] = useFetchRuleById({ setIsLoading, currentRuleId });

  const [fileProcessing] = useFetchFileProcessing({ setIsLoading });

  const [fetchedResourcesData] = useFetchResources({
    currentDomainId,
    setIsLoading,
  });
  const [allResources] = useFetchAllResourcesWithoutDomain({
    setIsLoading,
    aggregation_type: "aggregated",
  });

  const [isReRunData, setIsReRunData] = useState<any>({
    executionID: null,
    isReRun: false,
    runId: queryParams.get("run-id"),
    runName: queryParams.get("run-name"),
    executionSubGroupId: queryParams.get("execution-sub-group-id"),
    executionSubGroupCode: queryParams.get("execution-sub-group-code"),
  });
  const [scoreConfigOverride, setScoreConfigOverride] = useState<Record<
    string,
    any
  > | null>(null);
  const [priorScoreConfig, setPriorScoreConfig] = useState<Record<
    string,
    any
  > | null>(null);
  const [linkedServices] = useFetchLinkedServices({ setIsLoading });
  // FIXED: Use ref to prevent infinite loop by maintaining stable array reference
  const previousConnectionKeyIdsRef = useRef<number[]>([]);
  // OPTIMIZATION: Extract required connection key IDs from all resource data and linked services
  // This replaces the inefficient useFetchConnectionKeysAll call with targeted fetching
  const requiredConnectionKeyIds = useMemo(() => {
    if (
      !resourcesData ||
      !Array.isArray(resourcesData) ||
      resourcesData.length === 0
    ) {
      return previousConnectionKeyIdsRef.current;
    }
    const allConnectionKeyIds = new Set<number>();
    // DIRECT EXTRACTION for ReRun data structure (same as ReRunValidateResourceDataTab)
    resourcesData.forEach((resource: any) => {
      // Main resource connection key
      if (resource.connection_key) {
        allConnectionKeyIds.add(resource.connection_key);
      }
      // Aggregation base resource connection key
      if (resource.aggregation_base_resource_data?.connection_key) {
        allConnectionKeyIds.add(
          resource.aggregation_base_resource_data.connection_key,
        );
      }
      // Additional base resource connection keys
      if (resource.additional_base_resource_data?.length > 0) {
        resource.additional_base_resource_data.forEach(
          (additionalResource: any) => {
            if (additionalResource?.connection_key) {
              allConnectionKeyIds.add(additionalResource.connection_key);
            }
          },
        );
      }
    });
    // Convert to array and filter out null/undefined values
    const finalConnectionKeyIds = Array.from(allConnectionKeyIds).filter(
      (id) => id !== null && id > 0,
    );
    // CRITICAL FIX: Only return new array if IDs have actually changed
    const sortedIds = Array.from(new Set(finalConnectionKeyIds)).sort(
      (a, b) => a - b,
    );
    const previousIds = previousConnectionKeyIdsRef.current;
    // Compare arrays to prevent infinite loops
    const idsChanged =
      sortedIds.length !== previousIds.length ||
      sortedIds.some((id, index) => id !== previousIds[index]);

    if (idsChanged) {
      previousConnectionKeyIdsRef.current = sortedIds;
      return sortedIds;
    } else {
      return previousConnectionKeyIdsRef.current;
    }
  }, [resourcesData, linkedServices, ruleData]);

  // Fetch only the specific connection keys needed (optimized API call)
  const [fetchedConnectionKeys] = useFetchAllConnectionKeyWithoutId({
    setIsLoading,
  });

  useEffect(() => {
    if (fetchedConnectionKeys) {
      setFetchedAllConnectionKeys(fetchedConnectionKeys);
    }
  }, [fetchedConnectionKeys, setFetchedAllConnectionKeys]);

  // Get the setter function from context (following linkedServicesData pattern)
  const { setAllSystemData } = useSystemContext();
  // Fetch system data directly in this component (following linkedServicesData pattern)
  const [systemData] = useFetchAllSystem({
    setIsLoading,
  });
  // Update context state when system data is fetched (following linkedServicesData pattern)
  useEffect(() => {
    if (systemData) {
      setAllSystemData(systemData);
    }
  }, [systemData, setAllSystemData]);

  const selectedExecutionId = queryParams.get("execution-id");
  const [rerunData] = useFetchComparisonRerunResultByRuleId({
    executionId:
      selectedExecutionId && selectedExecutionId !== "null"
        ? Number(selectedExecutionId)
        : null,
    setIsLoading,
  });

  // Extract resource column IDs from all resources and their additional resources
  // This enables column merging in LinkedServices component for ReRunRuleExecution workflow
  const resourceColumnIds = useMemo(() => {
    const colIds: number[] = [];
    const resourcesData =
      rerunData?.rule_execution_request?.request_body?.resource_data;

    if (!resourcesData || !Array.isArray(resourcesData)) {
      return colIds;
    }

    // Iterate through all main resources
    resourcesData.forEach((resource: any) => {
      // Add main resource column ID
      const mainColId = resource?.resource_column_details_id;
      if (mainColId && !colIds.includes(mainColId)) {
        colIds.push(mainColId);
      }

      // Add additional base resource column IDs (ReRun uses additional_base_resource_data)
      const additionalResources = resource?.additional_base_resource_data;
      if (additionalResources && Array.isArray(additionalResources)) {
        additionalResources.forEach((addRes: any) => {
          const addColId = addRes?.resource_column_details_id;
          if (addColId && !colIds.includes(addColId)) {
            colIds.push(addColId);
          }
        });
      }
    });

    return colIds;
  }, [rerunData]);

  // Fetch resource columns for all resources (main + additional + aggregation base)
  const [resourceColumns] = useFetchResourceColumnsByMultipleIds({
    resourceColIds: resourceColumnIds,
    setIsLoading,
  });

  // Set resource columns data in context for column merging in LinkedServices
  useEffect(() => {
    if (resourceColumns?.length > 0) {
      setResourceColumnsData(resourceColumns);
    }
  }, [resourceColumns, setResourceColumnsData]);

  const [expandedAccordion, setExpandedAccordion] = useState<any>([
    "all-resources",
  ]);
  const [validationMessage, setValidationMessage] = useState<any[]>([]);
  const [checkValidation, setCheckValidation] = useState(false);

  const [inlineVariables, setInlineVariables] = useState<any>({
    rule_variables: {
      filterRule: null,
      adhoc_queries: null,
    },
    resource_variables: [],
    comparison_research_query_variables: [],
  });
  const [isEditVariables, setIsEditVariables] = useState(false);
  const [isEditFilters, setIsEditFilters] = useState(false);
  const [filterFormData, setFilterFormData] = useState<
    FilterFormData | undefined
  >(undefined);
  const [initialInlineVariables, setInitialInlineVariables] = useState<any>({});
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [secondaryResourceData, setSecondaryResourceData] = useState<any[]>([]);

  // State variables for payload dialog
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [reRunExecutionPayload, setReRunExecutionPayload] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);

  const [isEditSecondaryResource, setIsEditSecondaryResource] = useState(false);
  const [mergeColumnsLength, setMergeColumnsLength] = useState(0);
  const [executionMode, setExecutionMode] = useState(true);

  // Research Query state management for overriding functionality
  const [researchQueryDetailData, setResearchQueryDetailData] = useState<any>(
    {},
  );
  const [originalResearchQueryDetailData, setOriginalResearchQueryDetailData] =
    useState<any>({});
  const [isEditResearchQuery, setIsEditResearchQuery] = useState(false);

  const isUpdateRepeatedResourcesCalled = useRef(false);

  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reRun = params.get("re-run");
    const executionId = params.get("execution-id");
    const runId = params.get("run-id");
    const runName = params.get("run-name");
    const executionSubGroupId = params.get("execution-sub-group-id");
    const executionSubGroupCode = params.get("execution-sub-group-code");
    if (reRun && executionId) {
      setIsReRunData({
        executionID: Number(executionId),
        isReRun: true,
        runId: runId !== "null" ? runId : null,
        runName: runName !== "null" ? runName : null,
        executionSubGroupId:
          executionSubGroupId && executionSubGroupId !== "null"
            ? executionSubGroupId
            : null,
        executionSubGroupCode:
          executionSubGroupCode && executionSubGroupCode !== "null"
            ? executionSubGroupCode
            : null,
      });
    } else {
      setIsReRunData({
        executionID: null,
        isReRun: false,
      });
    }
  }, [location]);

  useEffect(() => {
    if (allResources) {
      setAllResourcesData(allResources);
    }
  }, [allResources, setAllResourcesData]);

  // Pre-load the comparison scoring config snapshot from the prior execution
  // so the override accordion seeds its initial values from what was actually
  // used last time. A null result (no snapshot / 404) means the accordion
  // falls back to fetched defaults.
  useEffect(() => {
    if (!isReRunData?.executionID) return;
    let cancelled = false;
    getComparisonScore(Number(isReRunData.executionID)).then((data) => {
      if (cancelled) return;
      const snapshot =
        data && typeof data === "object" && data.config_snapshot
          ? data.config_snapshot
          : null;
      setPriorScoreConfig(snapshot);
    });
    return () => {
      cancelled = true;
    };
  }, [isReRunData?.executionID]);

  useEffect(() => {
    if (linkedServices) {
      setLinkedServicesData(linkedServices);
    }
  }, [linkedServices, setLinkedServicesData]);

  useEffect(() => {
    if (fileProcessing) {
      setFileProcessingData(fileProcessing);
    }
  }, [fileProcessing, setFileProcessingData]);

  const addSecondaryResourceDetails = (
    resource: any,
    secondaryResourceData: any[],
  ) => {
    const parentMatch = secondaryResourceData?.find(
      (secondaryItem) => secondaryItem?.parentResId === resource?.resource_id,
    );
    const secondaryMatch = secondaryResourceData?.find(
      (secondaryItem) =>
        secondaryItem?.secondaryResId === resource?.resource_id &&
        secondaryItem?.parentResId === resource?.parentResId,
    );

    let updatedResource = { ...resource };

    if (parentMatch) {
      updatedResource = {
        ...updatedResource,
        secondary_merge_resource: parentMatch,
      };
    }

    if (secondaryMatch) {
      updatedResource = {
        ...updatedResource,
        isSecondaryResource: true,
        parentResId: secondaryMatch?.parentResId,
      };
    }

    return updatedResource;
  };

  /**
   * Set run parametes when re run data update
   */
  useEffect(() => {
    if (rerunData) {
      const severityColumnNames = {
        high:
          rerunData.rule_execution_request?.request_body
            ?.severity_column_names?.["1"] || null,
        medium:
          rerunData.rule_execution_request?.request_body
            ?.severity_column_names?.["2"] || null,
        low:
          rerunData.rule_execution_request?.request_body
            ?.severity_column_names?.["3"] || null,
      };

      setSeverityColumnNames(severityColumnNames);

      //Set values in run parameters
      const requestBody = rerunData?.rule_execution_request?.request_body || {};
      const queryParams = requestBody?.query_params || {};
      const runInstance = requestBody?.run_instance || {};
      const executionSubGroup = requestBody?.execution_sub_group || {};
      const severity = requestBody?.validation_severity_level;

      // Start with defaults, override with values from previous execution
      const defaults = getDefaultRunParameters("ruleExecution");
      setRunParameters({
        ...defaults,
        // Override backend fields from previous execution's query params
        ...Object.fromEntries(
          Object.keys(queryParams)
            .filter((key) => key in defaults)
            .map((key) => [key, queryParams[key] ?? defaults[key]]),
        ),
        // Ensure impact_issues is false when store_all_data_columns is off
        impact_issues:
          (queryParams.store_all_data_columns ?? true)
            ? (queryParams.impact_issues ?? false)
            : false,

        // FE-only fields
        validation_severity_level:
          severity === 1 ? "high" : severity === 2 ? "medium" : "low",

        run_instance: {
          run_id: runInstance.run_id || 1,
          run_name: runInstance.run_name || "Legacy",
        },
        execution_sub_group: {
          execution_sub_group_id: executionSubGroup.execution_sub_group_id || 0,
          execution_sub_group_code:
            executionSubGroup.execution_sub_group_code || "",
        },
        pull_latest_files:
          rerunData?.rule_execution_request?.request_body?.pull_latest_files ??
          false,
        pivot_results:
          rerunData?.rule_execution_request?.request_body?.pivot_results ??
          false,
        isCustomiseSeverity:
          severityColumnNames.high ||
          severityColumnNames.medium ||
          severityColumnNames.low
            ? true
            : false,
      });

      // Set values in run storage parameters
      setResultStorageParameters({
        ...defaultResultStorageParameters,
        linked_service_id:
          rerunData.rule_execution_request?.request_body?.output_storage_params
            ?.output_storage_linked_service_id,
        linked_service_code:
          rerunData.rule_execution_request?.request_body?.output_storage_params
            ?.output_storage_linked_service_code,
        connection_key_id:
          rerunData.rule_execution_request?.request_body?.output_storage_params
            ?.output_storage_connection_key,
        file_path:
          rerunData.rule_execution_request?.request_body?.output_storage_params
            ?.output_storage_base_file_path,
        validation_execution_report_name:
          rerunData.rule_execution_request?.request_body
            ?.validation_execution_report_name,
        rule_execution_report_name:
          rerunData.rule_execution_request?.request_body
            ?.rule_execution_report_name,
      });

      setInlineVariables(
        rerunData.rule_execution_request?.request_body?.inline_variables,
      );
      const updatedInlineVariables = {
        ...rerunData.rule_execution_request?.request_body?.inline_variables,
        resource_variables:
          rerunData.rule_execution_request?.request_body?.inline_variables?.resource_variables?.map(
            (resource: any) => {
              const resourceData =
                rerunData.rule_execution_request?.request_body?.resource_data?.find(
                  (data: any) => data.resource_id === resource.resource_id,
                );

              return {
                ...resource,
                resource_name:
                  resourceData?.resource_name || resource.resource_name,
              };
            },
          ) || [],
        rule_variables: {
          filterRule: {
            ...rerunData.rule_execution_request?.request_body?.inline_variables
              ?.rule_variables,
          },
        },
      };
      setViewInlineVariables(updatedInlineVariables);

      if (rerunData?.rule_execution_request?.request_body?.inline_variables)
        setInitialInlineVariables((prev: any) =>
          JSON.parse(
            JSON.stringify(
              rerunData.rule_execution_request?.request_body?.inline_variables,
            ),
          ),
        );

      const filter_rules =
        rerunData.rule_execution_request?.request_body?.filter_rules;

      setFilterFormData({
        filter_rules: filter_rules,
      });

      const secMergeResource =
        rerunData?.rule_execution_request?.request_body?.resource_data
          ?.filter(
            (resource: { secondary_merge_resource: any }) =>
              resource?.secondary_merge_resource,
          )
          ?.map(
            (resource: {
              resource_id: any;
              secondary_merge_resource: { resource_id: any };
            }) => ({
              parentResId: resource?.resource_id || null,
              secondaryResId:
                resource?.secondary_merge_resource?.resource_id || null,
              secondary_merge_resource:
                resource?.secondary_merge_resource || null,
            }),
          ) || [];
      const resourceDataWithSecMerge =
        rerunData?.rule_execution_request?.request_body?.resource_data.map(
          (item: any) => {
            const updatedItem = addSecondaryResourceDetails(
              item,
              secMergeResource,
            );

            // If type is 'sftp', rename resource_path to remote_directory
            if (item?.resource_type === "sftp" && item?.resource_path) {
              const result = {
                ...updatedItem,
                remote_directory: item?.resource_path,
                isSecondaryResource: item?.is_secondary_resource,
                id: item?.resource_id,
                type: item?.resource_type,
                aggregation_base_resource_data: {
                  ...updatedItem.aggregation_base_resource_data,
                  type: updatedItem.aggregation_base_resource_data
                    ?.resource_type,
                },
              };
              return result;
            }

            return {
              ...updatedItem,
              isSecondaryResource: item?.is_secondary_resource,
              id: item?.resource_id,
              type: item?.resource_type,
              aggregation_base_resource_data: {
                ...updatedItem.aggregation_base_resource_data,
                type: updatedItem.aggregation_base_resource_data?.resource_type,
              },
            };
          },
        );
      setResourcesData(resourceDataWithSecMerge);
      setSecondaryResourceData(secMergeResource);
      setExecutionName(
        rerunData.rule_execution_request?.request_body?.execution_name || "",
      );
      if (rerunData.rule_execution_request?.request_body?.research_queries) {
        const researchQueryData = {
          research_queries:
            rerunData.rule_execution_request?.request_body?.research_queries,
        };
        // Set research query detail data for overriding functionality
        setResearchQueryDetailData(researchQueryData);
        setOriginalResearchQueryDetailData(
          JSON.parse(JSON.stringify(researchQueryData)),
        );
      }
    }
  }, [rerunData]);

  useEffect(() => {
    setCurrentDomainId(ruleData?.domain_id);
    const adhocQueriesInlineVariables: any = {};

    ruleData?.rule_schema?.adhoc_queries?.forEach(
      (obj: { inline_variables: { name: any; value: any }[] }) => {
        obj.inline_variables?.forEach(({ name, value }) => {
          adhocQueriesInlineVariables[name] = value;
        });
      },
    );
  }, [ruleData]);
  // Update hasChanges when formData or fileData changes
  useEffect(() => {
    const inlineVariablesChanged =
      JSON.stringify(inlineVariables) !==
      JSON.stringify(initialInlineVariables);

    setHasChanges(inlineVariablesChanged);
  }, [inlineVariables, initialInlineVariables]);

  useEffect(() => {
    //set primary merge column Length
    const columnLength: number = ruleData?.rule_schema?.merge_rule
      ?.primary_dataset?.merge_on
      ? ruleData?.rule_schema?.merge_rule?.primary_dataset?.merge_on?.length
      : 0;

    setMergeColumnsLength(columnLength);
  }, [ruleData, setViewInlineVariables]);

  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: ruleData?.name,
      id: ruleData?.id,
    });
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [ruleData]);

  const comparisonCheckNameOptions = useMemo<string[]>(() => {
    const domainNames = (
      ruleData?.rule_schema?.domain_comparison_rules || []
    ).map((r: any) => r.name);
    const customNames = (
      ruleData?.rule_schema?.custom_comparison_rules || []
    ).map((r: any) => r.name);
    return [...domainNames, ...customNames].filter(Boolean);
  }, [ruleData]);

  const getComparisonSymbol = (comparisonType: string | number) => {
    const symbolMap: { [key: string]: string } = {
      equals: "=",
      "not equals": "!==",
      "greater than": ">",
      "less than": "<",
    };
    return symbolMap[comparisonType] || comparisonType;
  };
  const getResourceNamebyId = (
    resourceId: number | string | null | undefined,
  ) => {
    if (!resourcesData) {
      return undefined;
    }
    const matchingResource = resourcesData?.find(
      (resource: { resource_id: string | number | null | undefined }) =>
        resource.resource_id === resourceId,
    );
    return matchingResource ? matchingResource?.resource_name : undefined;
  };
  const getFilePreProcessing = (id: string | number | undefined) => {
    const filePreProcessing = fileProcessingData?.find(
      (file: { id: string | number | null | undefined }) => file.id === id,
    );
    return filePreProcessing
      ? filePreProcessing.file_processing_attributes
      : undefined;
  };
  const getApiDefinition = (resource: any) => {
    let user_name = resource?.api_definition?.user_name || null;
    let password = resource?.api_definition?.password || null;
    let bearer_token = resource?.api_definition?.bearer_token || null;
    let api_key = resource?.api_definition?.api_key || null;
    let oauth_client_id = resource?.api_definition?.oauth_client_id || null;
    let oauth_client_secret =
      resource?.api_definition?.oauth_client_secret || null;
    let oauth_url = resource?.api_definition?.oauth_url || null;
    let method = resource?.api_definition?.method || "get";
    let content_type =
      resource?.api_definition?.content_type || "application/json";
    let body = resource?.api_definition?.body || null;
    let query_params = resource?.api_definition?.query_params || null;
    let url_params = resource?.api_definition?.url_params || null;
    let url = resource?.api_definition?.url || null;
    let request_timeout = resource?.api_definition?.request_timeout || 0;
    return {
      user_name,
      password,
      bearer_token,
      api_key,
      oauth_client_id,
      oauth_client_secret,
      oauth_url,
      method,
      content_type,
      body,
      query_params,
      url_params,
      url,
      request_timeout,
    };
  };
  const getResourceDefinition = (resource: any) => {
    let connection_key = resource?.connection_key || null;
    let container_name = resource?.container_name || null;
    let resource_path =
      resource?.resource_type === "sftp"
        ? resource?.remote_directory
        : resource?.resource_path || null;
    let file_name = resource?.file_name || null;
    let excel_sheet_name = resource?.excel_sheet_name || null;
    let column_delimiter = resource?.column_delimiter || null;
    let sql_query = resource?.sql_query || null;
    let use_multi_thread_reader = resource?.use_multi_thread_reader || false;
    let column_name_to_partition_on_sql_query =
      resource?.column_name_to_partition_on_sql_query || null;

    return {
      connection_key,
      container_name,
      resource_path,
      file_name,
      excel_sheet_name,
      column_delimiter,
      sql_query,
      use_multi_thread_reader,
      column_name_to_partition_on_sql_query,
    };
  };

  const processAdditionalResourceData = (additionalResourceData: any) => {
    if (!Array.isArray(additionalResourceData)) {
      return null;
    }
    const res = additionalResourceData
      .map((resource) => getAdditionalResourceData(resource))
      .filter(Boolean);
    return res;
  };

  const getAdditionalResourceData = (resourceData: any) => {
    const resource: any = resourceData;

    const type = resource?.type || resource?.resource_type;

    const {
      connection_key,
      container_name,
      resource_path,
      file_name,
      excel_sheet_name,
      column_delimiter,
      sql_query,
      use_multi_thread_reader,
      column_name_to_partition_on_sql_query,
    } = getResourceDefinition(resource);
    const {
      user_name,
      password,
      bearer_token,
      api_key,
      oauth_client_id,
      oauth_client_secret,
      oauth_url,
      method,
      content_type,
      body,
      query_params,
      url_params,
      url,
      request_timeout,
    } = getApiDefinition(resource);
    const additional_base_resource_data =
      resource?.additional_base_resource_data
        ? processAdditionalResourceData(resource?.additional_base_resource_data)
        : null;

    let resourceObject: any = {
      aggregation_type: resource?.aggregation_type,
      resource_name: resource?.resource_name,
      resource_column_details_id:
        resource?.additional_properties?.resource_column_details_id,
      file_processing_id: resource?.file_processing_id,
      is_secondary_resource: resource?.isSecondaryResource
        ? resource?.isSecondaryResource
        : false,
      resource_id: resource?.resource_id || null,
      linked_service_id: resource?.linked_service_id || null,
      linked_service_code: resource?.linked_service_code || null,
      connection_key,
      sql_query,
      container_name,
      file_name,
      excel_sheet_name,
      column_delimiter,
      use_multi_thread_reader,
      column_name_to_partition_on_sql_query,
      additional_base_resource_data,
      filter_rules: resource?.filter_rules,
      file_pre_processing:
        sqlDatabaseType.includes(type) || apiType.includes(type)
          ? null
          : getFilePreProcessing(resource?.file_processing_id),
      aggregation_properties:
        resource?.aggregation_type === "flat"
          ? null
          : resource?.aggregation_properties || null,
      aggregation_base_resource_data: null,
      api_request: {
        user_name,
        password,
        bearer_token,
        api_key,
        oauth_client_id,
        oauth_client_secret,
        oauth_url,
        method,
        content_type,
        body,
        query_params,
        url_params,
        url,
        request_timeout,
      },
    };

    if (resource_path) {
      resourceObject.resource_path = resource_path;
    }
    //check required validation
    checkRequiredValidation(resource, resourceObject);

    if (
      Object.keys(resourceObject).length === 0 &&
      resourceObject.constructor === Object
    ) {
      return null;
    }
    return Object.keys(resourceObject).length > 1 ? resourceObject : null;
  };

  /**
   * Build re-run execution payload
   * This function extracts payload building logic into a pure function
   * that can be called independently for viewing/copying payload
   */
  const buildReRunExecutionPayload = () => {
    // Build output_storage_params
    let output_storage_params = {
      output_storage_linked_service_id:
        resultStorageParameters?.linked_service_id || null,
      output_storage_linked_service_code:
        resultStorageParameters?.linked_service_code || null,
      output_storage_connection_key:
        resultStorageParameters?.connection_key_id || null,
      output_storage_base_file_path: resultStorageParameters?.file_path || null,
    };

    const isOutputStorageParamsNull = Object.values(output_storage_params).some(
      (value) => value === null,
    );

    // Build run_instance
    const run_instance = {
      run_id: runParameters?.run_instance.run_id || 1,
      run_name: runParameters?.run_instance.run_name || "Legacy",
    };

    // Build execution_sub_group
    const execution_sub_group = {
      execution_sub_group_id:
        runParameters?.execution_sub_group?.execution_sub_group_id || 0,
      execution_sub_group_code:
        runParameters?.execution_sub_group?.execution_sub_group_code || "",
    };

    // Calculate severity level
    const severity_level =
      runParameters?.validation_severity_level === "high"
        ? 1
        : runParameters?.validation_severity_level === "medium"
          ? 2
          : 3;

    // Get report names
    const rule_execution_report_name =
      resultStorageParameters?.rule_execution_report_name || null;

    const validation_execution_report_name =
      resultStorageParameters?.validation_execution_report_name || null;

    // Build inline_variables
    const inline_variables = {
      comparison_research_query_variables:
        viewInlineVariables?.comparison_research_query_variables,
      resource_variables:
        viewInlineVariables?.resource_variables?.length >= 0
          ? viewInlineVariables?.resource_variables.map((item: any) => {
              return {
                resource_id: item?.resource_id,
                resource_vars: item?.resource_vars,
              };
            })
          : [],
      rule_variables: {
        ...viewInlineVariables?.rule_variables?.filterRule,
        ...viewInlineVariables?.rule_variables?.adhoc_queries,
      },
    };

    // Build severity column names
    let severityColumnNamesPayload = {
      1: severityColumnNames.high,
      2: severityColumnNames.medium,
      3: severityColumnNames.low,
    };
    const severity_column_names = Object.fromEntries(
      Object.entries(severityColumnNamesPayload).filter(([_, v]) => v !== null),
    );

    const cleanedResearchQueries =
      (researchQueryDetailData?.research_queries?.length > 0 &&
        researchQueryDetailData?.research_queries?.map(
          ({ updatedQuery, ...rest }: any) => rest,
        )) ||
      [];

    // Build base payload with always-required fields
    const reqBody: any = {
      rule_execution_request: {
        request_body: {
          execution_name: executionName,
          resource_data: resourcesData.map((resource: any) => {
            const type = resource?.type || resource?.resource_type;
            let connection_key = resource?.connection_key || null;
            let container_name = resource?.container_name || null;
            let resource_path =
              resource?.resource_type === "sftp"
                ? resource?.remote_directory || resource?.resource_path
                : resource?.resource_path || null;
            let file_name = resource?.file_name || null;
            let excel_sheet_name = resource?.excel_sheet_name || null;
            let column_delimiter = resource?.column_delimiter || null;
            let sql_query = resource?.sql_query || null;
            let use_multi_thread_reader =
              resource?.use_multi_thread_reader || false;
            let column_name_to_partition_on_sql_query =
              resource?.column_name_to_partition_on_sql_query || null;
            let use_parquet_export = resource?.use_parquet_export || false;
            let parquet_export_definition = use_parquet_export
              ? resource?.parquet_export_definition || null
              : null;
            let additional_base_resource_data = null;

            additional_base_resource_data =
              resource.additional_base_resource_data !== null
                ? resource.additional_base_resource_data?.length > 0
                  ? processAdditionalResourceData(
                      resource?.additional_base_resource_data,
                    )
                  : null
                : null;
            const {
              user_name,
              password,
              bearer_token,
              api_key,
              oauth_client_id,
              oauth_client_secret,
              oauth_url,
              method,
              content_type,
              body,
              query_params,
              url_params,
              url,
              request_timeout,
            } = getApiDefinition(resource);

            let resourceObject: any = {
              aggregation_type: resource?.aggregation_type,
              resource_name: resource?.resource_name,
              resource_column_details_id:
                resource?.additional_properties?.resource_column_details_id,
              file_processing_id: resource?.file_processing_id,
              is_secondary_resource: resource?.isSecondaryResource
                ? resource?.isSecondaryResource
                : false,
              resource_id: resource?.resource_id || null,
              linked_service_id: resource?.linked_service_id || null,
              linked_service_code: resource?.linked_service_code || null,
              connection_key,
              sql_query,
              container_name,
              file_name,
              excel_sheet_name,
              column_delimiter,
              use_multi_thread_reader,
              column_name_to_partition_on_sql_query,
              use_parquet_export,
              parquet_export_definition,
              additional_base_resource_data,
              filter_rules: resource?.filter_rules,
              file_pre_processing:
                sqlDatabaseType.includes(type) || apiType.includes(type)
                  ? null
                  : getFilePreProcessing(resource?.file_processing_id),
              aggregation_properties:
                resource?.aggregation_type === "flat"
                  ? null
                  : resource?.aggregation_properties || null,
              aggregation_base_resource_data:
                resource?.aggregation_type === "flat"
                  ? null
                  : getAdditionalResourceData(
                      resource?.aggregation_base_resource_data,
                    ) || null,
              api_request: {
                user_name,
                password,
                bearer_token,
                api_key,
                oauth_client_id,
                oauth_client_secret,
                oauth_url,
                method,
                content_type,
                body,
                query_params,
                url_params,
                url,
                request_timeout,
              },
              ...getSecondaryMergeResourcePayloadFields(resource),
            };

            if (resource_path) {
              resourceObject.resource_path = resource_path;
            }

            checkRequiredValidation(resource, resourceObject);

            resourceObject = Object.fromEntries(
              Object.entries(resourceObject).filter(([_, v]) => v !== null),
            );

            return resourceObject;
          }),
          filter_rules: filterFormData?.filter_rules ?? [],
          pull_latest_files: runParameters?.pull_latest_files ?? false,
          pivot_results: runParameters?.pivot_results ?? false,
          query_params: {
            ...getQueryParams("ruleExecution", runParameters),
            // Override with values from previous execution data
            no_of_sample_validation_errors:
              rerunData?.rule_execution_request?.request_body?.query_params
                ?.no_of_sample_validation_errors ?? 0,
            no_of_errors_in_filter_record_output_files:
              rerunData?.rule_execution_request?.request_body?.query_params
                ?.no_of_errors_in_filter_record_output_files ?? 0,
            use_merge_on_keys_as_unique_keys:
              rerunData?.rule_execution_request?.request_body?.query_params
                ?.use_merge_on_keys_as_unique_keys ?? true,
          },
          validation_severity_level: severity_level,
          run_instance: run_instance,
          execution_sub_group: execution_sub_group,
        },
        research_queries: cleanedResearchQueries,
        additional_info_for_all_resources:
          rerunData?.rule_execution_request?.additional_info_for_all_resources,
      },
      rule_id: rerunData?.rule_id,
      rule_version: rerunData?.rule_version,
    };

    // Include output_storage_params if not null
    if (!isOutputStorageParamsNull) {
      reqBody.rule_execution_request.request_body.output_storage_params =
        output_storage_params;
    }

    // Include rule_execution_report_name if not null
    if (rule_execution_report_name !== null) {
      reqBody.rule_execution_request.request_body.rule_execution_report_name =
        rule_execution_report_name;
    }

    // Include validation_execution_report_name if not null
    if (validation_execution_report_name !== null) {
      reqBody.rule_execution_request.request_body.validation_execution_report_name =
        validation_execution_report_name;
    }

    // Include severity_column_names if configured
    if (Object.keys(severity_column_names).length > 0) {
      reqBody.rule_execution_request.request_body.severity_column_names =
        severity_column_names;
    }

    // Include inline_variables if present
    if (
      inline_variables &&
      (inline_variables.comparison_research_query_variables ||
        inline_variables.resource_variables?.length > 0 ||
        Object.keys(inline_variables.rule_variables || {}).length > 0)
    ) {
      reqBody.rule_execution_request.request_body.inline_variables =
        inline_variables;
    }

    // Attach comparison scoring overrides only when scoring is enabled and the
    // user has modified values in the override accordion. The override is
    // nested under `comparison_score_params` to match the engine schema.
    if (runParameters?.generate_score && scoreConfigOverride) {
      reqBody.rule_execution_request.request_body.score_config_params = {
        comparison_score_params: scoreConfigOverride,
      };
    }

    return reqBody;
  };

  // Handle copying payload to clipboard
  const handleCopyPayload = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      const payload = buildReRunExecutionPayload();
      const formattedPayload = JSON.stringify(payload, null, 2);
      await navigator.clipboard.writeText(formattedPayload);
      showToast("JSON copied to clipboard!", "success");
    } catch (err) {
      console.error("Failed to copy JSON:", err);
      showToast("Failed to copy JSON to clipboard", "error");
    }
  };

  // Handle validating payload via the engine validate-request endpoint
  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: "/rules/rerun",
        method: "POST",
        body: payload,
        query_params: {},
        url_params: {},
      };
      const response = await validateEngineRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast("Payload is valid!", "success");
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    const executeRule = () => {
      setValidationMessage([]);

      // Validate inline variables using viewInlineVariables (current state)
      // This ensures validation works even when inline_variables is not in the differential payload
      const ruleVariables = {
        ...viewInlineVariables?.rule_variables?.filterRule,
        ...viewInlineVariables?.rule_variables?.adhoc_queries,
      };

      if (ruleVariables && Object.keys(ruleVariables).length > 0) {
        Object.keys(ruleVariables).forEach((key) => {
          if (ruleVariables[key] === "") {
            setValidationMessage((prev) => [
              ...prev,
              {
                id: `Rule Variables`,
                message: `Please enter value for ${key}`,
              },
            ]);
          }
        });
      }

      if (
        viewInlineVariables?.resource_variables &&
        viewInlineVariables.resource_variables.length > 0
      ) {
        viewInlineVariables.resource_variables.forEach(
          (resource: { resource_vars: any; resource_name: any }) => {
            const resourceVars = resource.resource_vars;
            if (resourceVars) {
              Object.keys(resourceVars).forEach((key) => {
                if (resourceVars[key] === "") {
                  setValidationMessage((prev) => [
                    ...prev,
                    {
                      id: `Resource ${resource.resource_name} Variables`,
                      message: `Please enter value for ${key}`,
                    },
                  ]);
                }
              });
            }
          },
        );
      }

      if (
        viewInlineVariables?.comparison_research_query_variables &&
        Object.keys(viewInlineVariables.comparison_research_query_variables)
          .length > 0
      ) {
        const researchQueryVariables =
          viewInlineVariables.comparison_research_query_variables;
        if (researchQueryVariables) {
          Object.keys(researchQueryVariables).forEach((key) => {
            if (researchQueryVariables[key] === "") {
              setValidationMessage((prev) => [
                ...prev,
                {
                  id: `Research Query Variables`,
                  message: `Please enter value for ${key}`,
                },
              ]);
            }
          });
        }
      }

      // Build the payload after validation using the extracted function
      const reqBody = buildReRunExecutionPayload();
      setRuleExecutionPayload(reqBody);
    };
    if (checkValidation) executeRule();
  }, [checkValidation]);

  useEffect(() => {
    if (
      validationMessage.length === 0 &&
      ruleExecutionPayload &&
      Object.keys(ruleExecutionPayload).length > 0
    ) {
      setIsBackdropLoading(true);
      if (executionMode) {
        backgroundExecuteRuleReRun(ruleExecutionPayload)
          .then(() => {
            showToast(
              "Your re-run rule execution is in the queue. We will notify you once it's done!",
              "warn",
            );
            navigate(`/rules-execution-history`);
          })
          .catch((error: any) => {
            console.error(error);
          })
          .finally(() => {
            setCheckValidation(false);
            setRuleExecutionPayload({});
            setIsBackdropLoading(false);
          });
      } else {
        executeRuleReRun(ruleExecutionPayload)
          .then((response: any) => {
            showToast("Rule Re-Run successfully!", "success");
            navigate(
              `/rules-execution-history/${response?.rule_id}/${response?.id}/dashboard`,
              {
                state: {
                  rowData: response,
                  isRuleExecutionResponse: true,
                },
              },
            );
          })
          .catch((error: any) => {
            console.error(error);
          })
          .finally(() => {
            setCheckValidation(false);
            setRuleExecutionPayload({});
            setIsBackdropLoading(false);
          });
      }
    } else if (validationMessage.length > 0) {
      const temp = (
        <div>
          Not all fields are configured. Please check the following fields:
          {validationMessage.map((msg) => (
            <div>
              <span style={{ fontWeight: 900 }}>{msg.id}</span>-{msg.message}
            </div>
          ))}
        </div>
      );
      showToast(temp, "warning");
      setCheckValidation(false);
      return;
    }
  }, [validationMessage, ruleExecutionPayload]);

  const checkRequiredValidation = (resource: any, resourceObject: any) => {
    const type = resource?.type || resource?.resource_type;
    const fieldNamesMap: { [key: string]: string } = {
      connection_key: "Connection Key",
      container_name: "Container Name",
      resource_path: "Resource Path",
      file_name: "File Name",
      column_delimiter: "Column Delimiter",
      base_resource_id: "Base resource",
      base_resource_columns: "Base Resource Columns",
      aggregation_query: "Aggregation Query",
      sql_query: "Sql Query",
      user_name: "Username",
      password: "Password",
      url: "Url",
      request_timeout: "Request Timeout",
      bearer_token: "Bearer Token",
      api_key: "Api Key",
      oauth_client_id: "Oauth Client Id",
      oauth_client_secret: "Oauth Client Secret",
      oauth_url: "Oauth Url",
    };
    if (resource?.aggregation_type === "flat") {
      let requiredFields: any = [];
      if (sqlDatabaseType.includes(type)) {
        requiredFields = ["connection_key", "sql_query"];
      } else if (type === "sftp" || type === "local") {
        requiredFields = [
          "connection_key",
          "resource_path",
          "file_name",
          "column_delimiter",
        ];
      } else if (type === "blob") {
        requiredFields = [
          "connection_key",
          "resource_path",
          "file_name",
          "column_delimiter",
          "container_name",
        ];
      } else if (type === "basic_auth_api") {
        requiredFields = [
          "connection_key",
          "user_name",
          "password",
          "url",
          "request_timeout",
        ];
      } else if (type === "token_auth_api") {
        requiredFields = [
          "connection_key",
          "bearer_token",
          "url",
          "request_timeout",
        ];
      } else if (type === "key_auth_api") {
        requiredFields = [
          "connection_key",
          "api_key",
          "url",
          "request_timeout",
        ];
      } else if (type === "oauth_api") {
        requiredFields = [
          "connection_key",
          "oauth_client_id",
          "oauth_client_secret",
          "oauth_url",
          "url",
          "request_timeout",
        ];
      }
      let missingFields: any;
      if (
        type === "basic_auth_api" ||
        type === "token_auth_api" ||
        type === "key_auth_api" ||
        type === "oauth_api"
      ) {
        missingFields = requiredFields?.filter(
          (field: any) => resourceObject?.api_request[field] === null,
        );
      } else {
        missingFields =
          requiredFields &&
          requiredFields.filter((field: any) => resourceObject[field] === null);
      }

      if (missingFields && missingFields.length > 0) {
        const missingFieldNames = missingFields.map(
          (field: any) => `${fieldNamesMap[field]}`,
        );
        setValidationMessage((prev) => [
          ...prev,
          {
            id: resource?.resource_name,
            message: missingFieldNames.join(", "),
          },
        ]);
        // return false;
      }
    } else if (
      resource?.aggregation_type === "aggregated" &&
      resourceObject.aggregation_properties !== null
    ) {
      const requiredFields: string[] = [
        "base_resource_id",
        "base_resource_columns",
        "aggregation_query",
      ];
      const missingFields = requiredFields.filter(
        (field) => resourceObject.aggregation_properties[field] === null,
      );

      if (missingFields.length > 0) {
        const missingFieldNames = missingFields.map(
          (field) => `${fieldNamesMap[field]}`,
        );
        setValidationMessage((prev) => [
          ...prev,
          {
            id: resource?.resource_name,
            message: missingFieldNames.join(", "),
          },
        ]);
      }
    }

    // Add for variables start
    if (
      resourceObject.inline_variables &&
      Object.keys(resourceObject.inline_variables).length > 0
    ) {
      Object.keys(resourceObject.inline_variables).forEach((key) => {
        if (resourceObject.inline_variables[key] === "") {
          setValidationMessage((prev) => [
            ...prev,
            {
              id: `${resource?.resource_name}-Variables`,
              message: `Please enter value for ${key}`,
            },
          ]);
        }
      });
    }
    // Add for variables end
  };
  const Summary = () => {
    return (
      <Box className="rule-execution-page compact-text-box-card">
        <Box className="text-box-card  pt-0" sx={{ marginBottom: "12px" }}>
          <Box>
            <h2 className="page-title">
              Domain <span>{ruleData?.domain_name}</span>
            </h2>
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6} lg={6}>
              <h4>Primary Resource</h4>
              {ruleData?.rule_schema?.merge_rule?.primary_dataset ? (
                <table className="table">
                  <thead>
                    <tr>
                      <th className="pe-40">Resource</th>
                      <th className="pe-40">Keys</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="pe-40">
                        {getResourceNamebyId(
                          ruleData?.rule_schema?.merge_rule?.primary_dataset
                            ?.resource_id,
                        )}
                      </td>
                      <td className="pe-40">
                        {ruleData?.rule_schema?.merge_rule?.primary_dataset?.merge_on?.join(
                          ", ",
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <p>No primary resource available</p>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <h4>Secondary Resource</h4>
              {ruleData?.rule_schema?.merge_rule?.secondary_datasets ? (
                <table className="table">
                  <thead>
                    <tr>
                      <th className="pe-40">Resource</th>
                      <th className="pe-40">Keys</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ruleData?.rule_schema?.merge_rule?.secondary_datasets?.map(
                      (dataset: {
                        resource_id: string | number | null | undefined;
                        merge_on: any[];
                      }) => (
                        <tr key={dataset.resource_id}>
                          <td className="pe-40">
                            {getResourceNamebyId(dataset.resource_id)}
                          </td>
                          <td className="pe-40">
                            {dataset.merge_on.join(", ")}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              ) : (
                <p>No secondary resource available</p>
              )}
            </Grid>
          </Grid>
        </Box>
        <Box className="text-box-card pt-0" sx={{ marginBottom: "12px" }}>
          <Box>
            <h2 className="page-title">
              {" "}
              Rule <span>{ruleData?.name}</span>
            </h2>
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6} lg={6}>
              <Grid item xs={12} sx={{ marginBottom: "12px" }}>
                <h4>Domain Comparison Rules</h4>
                {ruleData?.rule_schema?.domain_comparison_rules &&
                ruleData?.rule_schema?.domain_comparison_rules.length > 0 ? (
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="pe-20">Name</th>
                        <th className="pe-20">Column Name</th>
                        <th className="pe-20">Operator</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ruleData?.rule_schema?.domain_comparison_rules?.map(
                        (rule: {
                          name: string | number | null | undefined;
                          column_name: string;
                          comparison_type: string;
                        }) => (
                          <tr key={rule.name}>
                            <td className="pe-20">{rule.name}</td>
                            <td className="pe-20">{rule.column_name}</td>
                            <td className="pe-20">
                              {getComparisonSymbol(rule.comparison_type)}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                ) : (
                  <p>No domain comparison rules available</p>
                )}
              </Grid>
              <Grid item xs={12}>
                <h4 className="heading-hr">Resource Filter</h4>
                {ruleData?.rule_schema?.filter_rules &&
                ruleData?.rule_schema?.filter_rules.length > 0 ? (
                  <table className="table table-resource-filter mb-3">
                    <thead>
                      <tr>
                        <th className="pe-20 pb-10 width-180">Resource</th>
                        <th className="pe-20 pb-10">SQL Expression</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ruleData?.rule_schema?.filter_rules?.map((rule: any) => (
                        <tr key={rule.name}>
                          <td className="pe-20">{rule.name}</td>
                          <td className="pe-20 ">
                            <Box className="word-break-all">
                              {rule.sql_query}
                            </Box>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No Resource Filter available</p>
                )}
              </Grid>
              <Grid item xs={12}>
                <h4 className="heading-hr">Adhoc Queries</h4>
                {ruleData?.rule_schema?.adhoc_queries &&
                ruleData?.rule_schema?.adhoc_queries.length > 0 ? (
                  <table className="table table-resource-filter">
                    <thead>
                      <tr>
                        <th className="pe-20 pb-10 width-180">Name</th>
                        <th className="pe-20 pb-10">SQL Expression</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ruleData?.rule_schema?.adhoc_queries?.map(
                        (query: any, index: number) => (
                          <tr key={index}>
                            <td className="pe-20">{query.name}</td>
                            <td className="pe-20 ">
                              <Box className="word-break-all">
                                {query?.sql_query?.length > 100 ? (
                                  <ReadMoreLess
                                    data={query.sql_query}
                                    charLimit={100}
                                  />
                                ) : (
                                  query.sql_query || ""
                                )}
                              </Box>
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                ) : (
                  <p>No Adhoc Query available</p>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <h4>Custom Comparison Rules</h4>
              {ruleData?.rule_schema?.custom_comparison_rules &&
              ruleData?.rule_schema?.custom_comparison_rules.length > 0 ? (
                <table className="table w-100">
                  <thead>
                    <tr>
                      <th className="pe-20">Name</th>
                      <th className="pe-20">Column Name</th>
                      <th className="pe-20">Operator</th>
                      <th className="pe-20">Column Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ruleData?.rule_schema?.custom_comparison_rules?.map(
                      (rule: any) => (
                        <tr key={rule.name}>
                          <td className="pe-20">{rule.name}</td>
                          <td className="pe-20">
                            <Box
                              sx={{ maxWidth: "135px", wordWrap: "break-word" }}
                            >
                              {rule?.left_operand?.column_name}
                            </Box>
                          </td>
                          <td className="pe-20">
                            {getComparisonSymbol(rule.comparison_type)}
                          </td>
                          <td className="pe-20">
                            <Box
                              sx={{ maxWidth: "135px", wordWrap: "break-word" }}
                            >
                              {rule?.right_operand?.column_name}
                            </Box>
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              ) : (
                <p>No custom comparison rules available</p>
              )}
            </Grid>
          </Grid>
        </Box>
        <Box className="text-box-card pt-0 mb-0">
          <Box>
            <h2 className="page-title">
              <span>Description</span>
            </h2>
            {ruleData?.description ? (
              <span className="break-word">{ruleData?.description}</span>
            ) : (
              <span>No description available</span>
            )}
          </Box>
        </Box>
      </Box>
    );
  };
  // Nested accordions follow pattern: {resource_id}-{index} (ends with hyphen + digits)
  const isNestedAccordion = (id: string): boolean => /-\d+$/.test(id);
  const handleChangeAccordion = (panel: any) => (_: any, isExpanded: any) => {
    if (
      isResourceEdit !== "" ||
      isEditResource ||
      isEditVariables ||
      isEditResearchQuery
    ) {
      showToast("Please save changes first!!", "warning");
      return;
    }

    setExpandedAccordion((prev: any[]) => {
      //now also the research query accordion is added
      if (panel === "research-queries") {
        return isExpanded ? ["research-queries"] : [];
      }

      if (isExpanded) {
        if (!isNestedAccordion(panel)) {
          // Top-level accordion: close other top-level, keep nested if opening "all-resources"
          const nested = prev.filter((id) => isNestedAccordion(id));
          const keptNested = panel === "all-resources" ? nested : [];
          return [...keptNested, panel];
        } else {
          // Nested accordion: close siblings, ensure parent is open
          const withoutSiblings = prev.filter(
            (id) => !isNestedAccordion(id) || id === panel,
          );
          const withParent = withoutSiblings.includes("all-resources")
            ? withoutSiblings
            : [...withoutSiblings, "all-resources"];
          return withParent.includes(panel)
            ? withParent
            : [...withParent, panel];
        }
      } else {
        // Closing: just remove it
        return prev.filter((item: any) => item !== panel);
      }
    });
  };
  const handleViewResource = (domainId: any, resource: any, e: any) => {
    e.preventDefault();
    if (isResourceEdit !== "") {
      return;
    }
    navigate(`/resource/${domainId}/view/${resource}`);
  };

  const handleChangeVariable = (event: any, inline_type: string, id?: any) => {
    const { name, value } = event.target;
    if (id && inline_type === "resource_variables") {
      setInlineVariables((prev: any) => ({
        ...prev,
        resource_variables: prev.resource_variables.map((resource: any) => {
          if (resource.resource_id === id) {
            return {
              ...resource,
              resource_vars: {
                ...resource.resource_vars,
                [name]: value,
              },
            };
          }
          return resource;
        }),
      }));
    } else if (inline_type === "filterRule") {
      setInlineVariables((prev: any) => ({
        ...prev,
        rule_variables: {
          ...prev.rule_variables,
          filterRule: {
            ...prev.rule_variables.filterRule,
            [name]: value,
          },
        },
      }));
    } else if (inline_type === "adhoc_queries") {
      setInlineVariables((prev: any) => ({
        ...prev,
        rule_variables: {
          ...prev.rule_variables,
          adhoc_queries: {
            ...prev.rule_variables.adhoc_queries,
            [name]: value,
          },
        },
      }));
    } else if (inline_type === "comparison_research_query_variables") {
      setInlineVariables((prev: any) => ({
        ...prev,
        comparison_research_query_variables: {
          ...prev.comparison_research_query_variables,
          [name]: value,
        },
      }));
    }
  };
  const handleSecondaryMergeResourceDialog = () => {
    setSecondaryMergeResourceDialog(false);
  };

  const onSaveSecondaryMergeResource = () => {
    setSecondaryMergeResourceDialog(false);
  };
  const handleExecuteSubmit = async () => {
    try {
      await executionNameSchema.validate(
        { execution_name: executionName },
        {
          abortEarly: false,
        },
      );
      setCheckValidation(true);
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach(
        (error: { path: string | number; message: string }) => {
          newErrors[error.path] = error.message;
        },
      );
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };
  return (
    <>
      <Loader isLoading={isBackdropLoading} />
      <Loader isLoading={isLoading} />

      <div
        className="accordion-panel "
        style={{ marginBottom: "0", marginTop: "8px" }}
      >
        <Box>
          <ExecutionName
            handleChangeAccordion={handleChangeAccordion}
            errors={errors}
            setErrors={setErrors}
            executionName={executionName}
            setExecutionName={setExecutionName}
            executionMode={executionMode}
            setExecutionMode={setExecutionMode}
            runParameters={runParameters}
            setRunParameters={setRunParameters}
            setIsLoading={setIsLoading}
            isReRun={true}
          />
          <Accordion
            className="heading-bold box-shadow color-palette color-palette-1"
            expanded={expandedAccordion.includes("summary")}
            onChange={handleChangeAccordion("summary")}
          >
            <AccordionSummary
              aria-controls="panel1d-content"
              id="panel1d-header"
              expandIcon={<ExpandMoreIcon />}
            >
              Summary
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: "16px" }}>
              <Summary />
            </AccordionDetails>
          </Accordion>
          {/* Research Query Accordion */}
          {researchQueryDetailData?.research_queries?.length > 0 && (
            <Accordion
              className="mt-6 heading-bold box-shadow color-palette color-palette-1"
              expanded={expandedAccordion.includes("research-queries")}
              onChange={handleChangeAccordion("research-queries")}
            >
              <AccordionSummary
                aria-controls="research-queries-content"
                id="research-queries-header"
                expandIcon={<ExpandMoreIcon />}
              >
                Research Queries
              </AccordionSummary>
              <AccordionDetails sx={{ paddingTop: "16px" }}>
                <RuleResearchQuery
                  researchQuery={researchQueryDetailData}
                  setResearchQuery={setResearchQueryDetailData}
                  isFromExecution={true}
                  isEditResearchQuery={isEditResearchQuery}
                  setIsEditResearchQuery={setIsEditResearchQuery}
                  ruleDetailData={ruleData}
                  allowExecutionOverride={true}
                />
              </AccordionDetails>
            </Accordion>
          )}
          {resourcesData && resourcesData.length > 0 && (
            <>
              <Accordion
                className="mt-6 heading-bold box-shadow color-palette color-palette-2"
                expanded={expandedAccordion.includes("all-resources")}
                onChange={handleChangeAccordion("all-resources")}
              >
                <AccordionSummary
                  aria-controls="panel2d-content"
                  id="panel2d-header"
                  expandIcon={<ExpandMoreIcon />}
                >
                  Resource(s)
                </AccordionSummary>
                <AccordionDetails sx={{ paddingTop: "0px !important" }}>
                  {resourcesData.map((resource: any, index: number) => (
                    <>
                      <Accordion
                        className={`mt-6 heading-bold box-shadow color-palette color-palette-2-a
                          ${
                            resource?.aggregation_type === "additional"
                              ? "color-palette-2-c"
                              : resource?.aggregation_type === "aggregated"
                                ? "color-palette-2-b"
                                : ""
                          }
                          ${
                            resource?.isSecondaryResource
                              ? "execution-secondary-resource"
                              : ""
                          }`}
                        expanded={expandedAccordion.includes(
                          `${resource?.resource_id}-${index}`,
                        )}
                        onChange={handleChangeAccordion(
                          `${resource?.resource_id}-${index}`,
                        )}
                        key={index}
                      >
                        <AccordionSummary
                          aria-controls={`panel-${resource?.resource_id}-content`}
                          id={`panel-${resource?.resource_id}-header`}
                          expandIcon={<ExpandMoreIcon />}
                        >
                          <div className="custom-actions">
                            <div className="heading">
                              {
                                resourcesData?.find(
                                  (resource1: any) =>
                                    resource1?.resource_id ===
                                    resource?.resource_id,
                                )?.resource_name
                              }
                            </div>
                            <div className="action-btns">
                              <Tooltip
                                title="Navigate to view resource"
                                placement="top"
                              >
                                <button
                                  className="view-icon"
                                  onClick={(e) =>
                                    handleViewResource(
                                      ruleData.domain_id,
                                      resource?.id,
                                      e,
                                    )
                                  }
                                >
                                  <IconViewSvgWhite />
                                </button>
                              </Tooltip>
                            </div>
                          </div>
                        </AccordionSummary>
                        <AccordionDetails sx={{ paddingTop: "16px" }}>
                          <React.Fragment key={resource?.resource_id}>
                            <ReRunRuleExecutionTab
                              resourcesData={resourcesData}
                              resourceData={resourcesData?.find((res: any) =>
                                res?.isSecondaryResource
                                  ? res?.id === resource?.id &&
                                    res?.parentResId === resource?.parentResId
                                  : res?.resource_id === resource?.resource_id,
                              )}
                              isLoading={isBackdropLoading}
                              setResourcesData={setResourcesData}
                              setIsLoading={setIsLoading}
                              fileProcessingData={fileProcessingData}
                              fileStreamIndex={index}
                              setViewInlineVariables={setViewInlineVariables}
                              viewInlineVariables={viewInlineVariables}
                              isReRun={isReRunData?.isReRun}
                              fetchedConnectionKeys={fetchedConnectionKeys}
                            />
                          </React.Fragment>
                        </AccordionDetails>
                      </Accordion>
                    </>
                  ))}
                </AccordionDetails>
              </Accordion>
            </>
          )}

          <RunParameters
            runParameters={runParameters}
            setRunParameters={setRunParameters}
            isEditResource={isEditResource}
            setIsEditResource={setIsEditResource}
            setIsLoading={setIsLoading}
            severityColumnNames={severityColumnNames}
            setSeverityColumnNames={setSeverityColumnNames}
            expandedAccordion={
              expandedAccordion.includes("advanced-run-parameters")
                ? "advanced-run-parameters"
                : expandedAccordion.includes("standard-run-parameters")
                  ? "standard-run-parameters"
                  : ""
            }
            handleChangeAccordion={handleChangeAccordion}
            feature="ruleExecution"
            isReRun={isReRunData?.isReRun}
            // Only constrain secondary mappings in the original execution snapshot.
            originalUseSecondaryMergeResources={
              rerunData?.rule_execution_request?.request_body?.resource_data?.some(
                (resource: any) =>
                  !!resource?.secondary_merge_resource?.resource_id,
              )
                ? rerunData?.rule_execution_request?.request_body?.query_params
                    ?.use_secondary_merge_resources
                : undefined
            }
            resourcesData={resourcesData}
          />

          <ScoringConfigOverrideAccordion
            type="comparison"
            generateScore={!!runParameters?.generate_score}
            currentConfig={priorScoreConfig}
            expanded={expandedAccordion.includes("scoring-config-overrides")}
            onChange={handleChangeAccordion("scoring-config-overrides")}
            onOverrideChange={setScoreConfigOverride}
            disabled={isEditResource}
            nameOptions={comparisonCheckNameOptions}
          />

          <Accordion
            className="mt-6 heading-bold box-shadow color-palette color-palette-1"
            expanded={expandedAccordion.includes("result-storage-parameters")}
            onChange={handleChangeAccordion("result-storage-parameters")}
          >
            <AccordionSummary
              aria-controls="panel1d-content"
              id="panel1d-header"
              expandIcon={<ExpandMoreIcon />}
            >
              Result Storage Parameters
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: "16px" }}>
              <ResultStorageParameters
                resultStorageParameters={resultStorageParameters}
                setResultStorageParameters={setResultStorageParameters}
                isEditResource={isEditResource}
                setIsEditResource={setIsEditResource}
                setIsLoading={setIsLoading}
                isFromRule={true}
                isReRun={isReRunData?.isReRun}
              />
            </AccordionDetails>
          </Accordion>

          {((inlineVariables?.rule_variables?.adhoc_queries &&
            Object.keys(inlineVariables?.rule_variables?.adhoc_queries).length >
              0) ||
            (inlineVariables?.rule_variables?.filterRule &&
              Object.keys(inlineVariables?.rule_variables?.filterRule).length >
                0) ||
            (inlineVariables?.resource_variables &&
              inlineVariables?.resource_variables.length > 0)) && (
            <Accordion
              className="mt-6 heading-bold box-shadow color-palette color-palette-1"
              expanded={expandedAccordion.includes("inline-variables")}
              onChange={handleChangeAccordion("inline-variables")}
            >
              <AccordionSummary
                aria-controls="panel1d-content"
                id="panel1d-header"
                expandIcon={<ExpandMoreIcon />}
              >
                Variables
              </AccordionSummary>
              <AccordionDetails sx={{ paddingTop: "16px" }}>
                {!isEditVariables ? (
                  <RuleInlineVariables
                    handleChangeVariable={handleChangeVariable}
                    inlineVariables={viewInlineVariables}
                    isReadOnly={true}
                  />
                ) : (
                  <RuleInlineVariables
                    handleChangeVariable={handleChangeVariable}
                    inlineVariables={inlineVariables}
                    isReadOnly={false}
                  />
                )}
                {!isEditVariables ? (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md
                    sx={{ justifyContent: "flex-end", display: "flex" }}
                  >
                    <button
                      className="btn-nostyle icon-btn-edit"
                      onClick={() => {
                        setIsEditVariables(true);
                        setInlineVariables(viewInlineVariables);
                      }}
                    >
                      <IconBtnEditBase ariaLabel="Edit variables" />
                    </button>
                  </Grid>
                ) : (
                  <Grid
                    item
                    xs
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => {
                          setIsEditVariables(false);
                          setInlineVariables(viewInlineVariables);
                        }}
                        className="btn-orange btn-dark"
                      >
                        Cancel
                      </Button>
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => {
                          setViewInlineVariables(inlineVariables);
                          setIsEditVariables(false);
                        }}
                        className="btn-orange"
                        disabled={!hasChanges}
                      >
                        <SaveOutlinedIcon /> &nbsp; Save
                      </Button>
                    </Box>
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
          )}
          {ruleData?.rule_schema?.filter_rules &&
            ruleData?.rule_schema?.filter_rules.length > 0 && (
              <CustomAccordian
                expandId="filter-rules"
                title="Filter Rules"
                handleChangeAccordion={handleChangeAccordion}
                isEnabled={true}
                topMargin={6}
                className="color-palette color-palette-1"
              >
                {!isEditFilters ? (
                  <FilterRules formData={filterFormData} isViewOnly={true} />
                ) : (
                  <CustomMultipleResourceRuleFilters
                    formData={filterFormData}
                    resourcesData={resourcesData}
                    setFormData={setFilterFormData}
                    setViewInlineVariables={setViewInlineVariables}
                    isEditFilters={isEditFilters}
                    setIsEditFilters={setIsEditFilters}
                  />
                )}
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md
                  sx={{
                    justifyContent: "flex-end",
                    display: "flex",
                    marginTop: "8px",
                  }}
                >
                  {!isEditFilters && (
                    <button
                      className="btn-nostyle icon-btn-edit"
                      onClick={() => {
                        setIsEditFilters((prev) => !prev);
                      }}
                    >
                      <IconBtnEditBase ariaLabel="Edit filter rules" />
                    </button>
                  )}
                </Grid>
              </CustomAccordian>
            )}
          {ruleData?.rule_schema?.adhoc_queries &&
            ruleData?.rule_schema?.adhoc_queries.length > 0 && (
              <CustomAccordian
                expandId="adhoc-query"
                title="Adhoc Query"
                handleChangeAccordion={handleChangeAccordion}
                isEnabled={true}
                topMargin={8}
                className="color-palette color-palette-1"
              >
                <AddMultipleGridList
                  gridValue={ruleData?.rule_schema?.adhoc_queries}
                  setGridValue={() => {}}
                  isViewOnly={true}
                  buttonName=" "
                  errors={{}}
                  setErrors={() => {}}
                />
              </CustomAccordian>
            )}
          {!isEditResource && isResourceEdit === "" && (
            <FlexBetween
              gap="3rem"
              sx={{
                marginTop: 2,
                display: "flex",
                justifyContent: "flex-end",
                gap: "1rem",
              }}
            >
              {isAdmin && (
                <Button
                  className="btn-orange btn-dark"
                  onClick={() => {
                    const payload = buildReRunExecutionPayload();
                    setReRunExecutionPayload(payload);
                    setValidationResponse(null);
                    setIsPayloadDialogOpen(true);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0,
                    padding: "6px 16px",
                    minWidth: "auto",
                  }}
                >
                  <Box sx={{ paddingRight: "12px" }}>View JSON</Box>
                  <Box
                    sx={{
                      width: "1px",
                      height: "24px",
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  />
                  <Box
                    onClick={handleCopyPayload}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: "12px",
                      cursor: "pointer",
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: "18px" }} />
                  </Box>
                </Button>
              )}
              <Button
                type="submit"
                className="btn-orange"
                color="secondary"
                variant="contained"
                onClick={handleExecuteSubmit}
              >
                {isReRunData?.isReRun ? "Re-Execute" : "Execute"}
              </Button>
            </FlexBetween>
          )}
        </Box>
      </div>
      <SecondaryMergeResourceDialog
        secondaryMergeResourceDialog={secondaryMergeResourceDialog}
        handleSecondaryMergeResourceDialog={handleSecondaryMergeResourceDialog}
        resourceDataWithSecMerge={resourceDataWithSecMerge}
        setResourceDataWithSecMerge={setResourceDataWithSecMerge}
        onSaveSecondaryMergeResource={onSaveSecondaryMergeResource}
        fetchedResourcesData={fetchedResourcesData}
        setIsLoading={setIsLoading}
        isEditSecondaryResource={isEditSecondaryResource}
        mergeColumnsLength={mergeColumnsLength}
        fetchedConnectionKeys={fetchedConnectionKeys}
        secondaryResourceData={secondaryResourceData}
        setSecondaryResourceData={setSecondaryResourceData}
        setResourcesData={setResourcesData}
        resourcesData={resourcesData}
      />
      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        payload={reRunExecutionPayload}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        dialogTitle="Rule Re-Run Execution JSON"
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </>
  );
};

export default ReRunRuleExecution;
